'use client';

import Link from 'next/link';
import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { useContextTabsContext } from '@/features/context-tabs';
import { useAppSelector } from '@/store/hooks';
import { LogoutButton } from '@/ui';
import { buildStorageViewUrl } from '@/utils/storage';
import { Avatar, Popover, ChevronIcon } from '@web/ui';
import { getKeyboardShortcutLabel } from '@web/utils';

import styles from './SideNav.module.scss';

type UserNavItemProps = {
  showSideNavToggle?: boolean;
  isSideNavExpanded?: boolean;
  onToggleSideNav?: () => void;
};

export function UserNavItem({
  showSideNavToggle = false,
  isSideNavExpanded = false,
  onToggleSideNav,
}: UserNavItemProps) {
  const currentUser = useAppSelector((s) => s.auth.currentUser);
  const contextTabs = useContextTabsContext();
  const currentWorkspace = useAppSelector((s) => s.workspaces.currentWorkspace);

  const fullName =
    `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim() ||
    'User';
  const userRole = currentWorkspace?.role?.name ?? 'Member';
  const userAvatarModel = currentUser as
    | (typeof currentUser & {
        avatar_asset_key?: string | null;
        avatar_url?: string | null;
      })
    | null;
  const userAvatar =
    buildStorageViewUrl(userAvatarModel?.avatar_asset_key) ||
    userAvatarModel?.avatar_url ||
    currentUser?.avatar;
  const profileHref = currentWorkspace?.slug
    ? appRoutes.workspace.profile(currentWorkspace.slug).index
    : appRoutes.home;

  const showLabels = showSideNavToggle ? isSideNavExpanded : true;
  const sideNavShortcutLabel = getKeyboardShortcutLabel('B');
  const sideNavToggleLabel = isSideNavExpanded
    ? `Collapse sidebar (${sideNavShortcutLabel})`
    : `Expand sidebar (${sideNavShortcutLabel})`;
  const isSideNavCollapsedWithToggle = showSideNavToggle && !isSideNavExpanded;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      {isSideNavCollapsedWithToggle && onToggleSideNav ? (
        <button
          type="button"
          className={`action-item ${styles.sideNavToggleIconButton} ${styles.sideNavToggleOnly}`}
          onClick={onToggleSideNav}
          aria-label={sideNavToggleLabel}
          title={sideNavToggleLabel}
        >
          <span className={styles.sideNavToggleGlyph} aria-hidden>
            <ChevronIcon direction="right" />
          </span>
        </button>
      ) : (
        <div className={styles.userSectionRow}>
          <button
            type="button"
            className={`action-item ${styles.userActionItem} ${styles.userMenuButton}`}
            onClick={handleOpen}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label="User menu"
          >
            <span className={styles.iconWrapDual} aria-hidden>
              <Avatar name={fullName} src={userAvatar} size="medium" />
            </span>
            {showLabels ? (
              <span className={styles.userIdentity}>
                <span
                  className={`action-item-label truncate-sm ${styles.userName}`}
                  style={{
                    fontWeight: '600',
                  }}
                >
                  {fullName}
                </span>
                <span className={`truncate-sm ${styles.userRole}`}>
                  {userRole}
                </span>
              </span>
            ) : null}
          </button>

          {showSideNavToggle && onToggleSideNav ? (
            <button
              type="button"
              className={`action-item ${styles.sideNavToggleIconButton}`}
              onClick={onToggleSideNav}
              aria-label={sideNavToggleLabel}
              title={sideNavToggleLabel}
            >
              <span
                className={`${styles.sideNavToggleGlyph} ${isSideNavExpanded ? styles.sideNavToggleGlyphExpanded : ''}`}
                aria-hidden
              >
                <ChevronIcon direction="right" />
              </span>
            </button>
          ) : null}
        </div>
      )}

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <Avatar name={fullName} src={userAvatar} size="large" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong className="truncate-md" style={{ lineHeight: 1.2 }}>
                {fullName}
              </strong>
              <span
                className="truncate-md"
                style={{ fontSize: 12, opacity: 0.8 }}
              >
                {currentUser?.email}
              </span>
            </div>
          </div>

          <div className="action-items">
            <Link
              href={profileHref}
              className="action-item"
              prefetch
              onClick={(event) => {
                if (!contextTabs) return;
                event.preventDefault();
                handleClose();
                contextTabs.navigateInActiveTab(profileHref);
              }}
            >
              <span>My Profile</span>
            </Link>
          </div>

          <div style={{ marginTop: 8 }}>
            <LogoutButton variant="buttonStyle" />
          </div>
        </>
      </Popover>
    </div>
  );
}
